import { useMemo } from 'react';
import { Edit, FunctionField, TextField, TopToolbar, useNotify, usePermissions, useRecordContext, useRefresh, useTranslate } from 'react-admin';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import CustomList from '@/components/global/list/CustomList.jsx';
import { CustomDatagridConfigurable } from '@/components/global/list/CustomDatagridConfigurable.jsx';
import CustomDatagridHeader from '@/components/global/list/CustomDatagridHeader.jsx';
import CustomMorePopover from '@/components/global/buttons/CustomMorePopover.jsx';
import CustomEditModalButton from '@/components/global/buttons/CustomEditModalButton.jsx';
import CustomShowModalButton from '@/components/global/buttons/CustomShowModalButton.jsx';
import { SearchBar } from '@/components/global/list/searchBar.jsx';
import { SearchFilterInput } from '@/components/global/filters/SearchFilterInput.jsx';
import CustomFilterButton from '@/components/global/filters/CustomFilterButton.jsx';
import { CustomSelectColumnsButton } from '@/components/global/buttons/CustomSelectColumnsButton.jsx';
import CustomToolbarActionsPopover from '@/components/global/buttons/CustomToolbarActionsPopover';
import CustomCreateButton from '@/components/global/buttons/CustomCreateButton.jsx';
import CustomCreate from '@/components/global/list/CustomCreate.jsx';
import { RightDrawer } from '@/components/global/drawers/RightDrawer';
import { ReferenceArrayFilter, Select1or0Filter } from '@/components/global/filters/filters.jsx';
import { CustomBooleanField } from '@/components/global/fields/CustomBooleanField.jsx';
import ColumnsIcon from '@/assets/icons/columns-icon.svg?react';
import { ParameterModelsField } from '@/components/parameter/ParameterModelsField.jsx';
import { ParameterOverwritesField } from '@/components/parameter/ParameterOverwritesField.jsx';
import { ParameterDetailsContent } from '@/components/parameter/ParameterDetailsContent.jsx';
import { ExpandSignificantParameters } from '@/components/parameter/ExpandSignificantParameters.jsx';
import { ParameterForm } from '@/components/parameter/ParameterForm.jsx';
import { RefreshDeviceConfigButton } from '@/components/parameter/buttons/RefreshDeviceConfigButton.jsx';
import { ShowDeviceConfigListButton } from '@/components/device/buttons/ShowDeviceConfigListButton.jsx';
import { canViewDeviceConfig } from '@/helpers/device/deviceConfig.js';
import { transformParameterPayload } from '@/helpers/parameter/parameterValidation.js';
import { normalizeParameterRecord } from '@/helpers/parameter/parameterSubsets.js';
import { Palette } from '@/themes/themeOverrides/Palette';
import { useDrawer } from '@/composables/useDrawer.js';
import { canViewDetails } from '@/helpers/permissions/canViewDetails.js';
import { ParameterStatisticList } from '@/components/analytics/statisticList.jsx';

const SEARCH_FIELDS = {
    name: true,
    parameter: true,
    description: true,
    value: true,
};

const getFilters = (isMobile, translate) => [
    <SearchBar key='search' source='value' fields={SEARCH_FIELDS} label={translate('common.search')} alwaysOn />,
    <ReferenceArrayFilter
        key='models'
        source='models_any'
        reference='device-model'
        label={translate('resources.parameter.filters.model')}
        optionText='name'
        searchFields={['name', 'code']}
        forceDesktopOnMobile={true}
        alwaysOn={isMobile}
    />,
    <Select1or0Filter key='overrideCount' source='overrideCount' label={translate('resources.parameter.filters.hasException')} alwaysOn={isMobile} />,
    <Select1or0Filter key='isSignificant' source='isSignificant' label={translate('resources.parameter.fields.isSignificant')} alwaysOn={isMobile} />,
    <Select1or0Filter key='isActive' source='isActive' label={translate('resources.parameter.fields.isActive')} alwaysOn={isMobile} />,
];

const ListActions = () => {
    const { permissions } = usePermissions();
    const translate = useTranslate();

    return (
        <TopToolbar>
            <SearchFilterInput searchFields={SEARCH_FIELDS} />
            <CustomFilterButton />
            <CustomSelectColumnsButton startIcon={<ColumnsIcon />} style={{ margin: '0px -4px' }} />
            {canViewDeviceConfig(permissions) && (
                <CustomToolbarActionsPopover showOnMobile>
                    {permissions?.isAdmin ? <CustomCreateButton label={translate('resources.parameter.labels.create')} /> : null}
                    <RefreshDeviceConfigButton />
                    <ShowDeviceConfigListButton />
                </CustomToolbarActionsPopover>
            )}
        </TopToolbar>
    );
};

export const ParameterCreate = ({ handleClose }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const refresh = useRefresh();
    const { permissions } = usePermissions();

    if (!permissions?.isAdmin) {
        return null;
    }

    return (
        <RightDrawer handleClose={handleClose} title={translate('resources.parameter.labels.create')}>
            <CustomCreate
                handleClose={handleClose}
                transform={transformParameterPayload}
                mutationOptions={{
                    onSuccess: () => {
                        notify(translate('resources.parameter.notifications.created'));
                        refresh();
                        handleClose();
                    },
                }}
            >
                <ParameterForm handleClose={handleClose} />
            </CustomCreate>
        </RightDrawer>
    );
};

export const ParameterEdit = ({ handleClose = () => {}, elementId }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const refresh = useRefresh();
    const { permissions } = usePermissions();

    if (!permissions?.isAdmin) {
        return null;
    }

    return (
        <RightDrawer handleClose={handleClose} title={translate('resources.parameter.labels.edit')}>
            <Edit
                resource='parameter'
                mutationMode='pessimistic'
                id={elementId}
                actions={false}
                transform={transformParameterPayload}
                queryOptions={{
                    select: record => normalizeParameterRecord(record),
                }}
                mutationOptions={{
                    onSuccess: () => {
                        notify(translate('resources.parameter.notifications.updated'));
                        refresh();
                        handleClose();
                    },
                }}
                redirect={false}
            >
                <ParameterForm handleClose={handleClose} />
            </Edit>
        </RightDrawer>
    );
};

const MoreActionsPopover = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    const { permissions } = usePermissions();
    const { openDrawer } = useDrawer();
    const canShow = canViewDetails('parameter', permissions);

    if (!record || (!canShow && !permissions?.isAdmin)) {
        return null;
    }

    return (
        <CustomMorePopover record={record}>
            {canShow && <CustomShowModalButton label={translate('resources.parameter.labels.show')} onOpen={() => openDrawer(record.id, 'parameter-show')} />}
            {permissions?.isAdmin && (
                <CustomEditModalButton label={translate('resources.parameter.labels.edit')} onOpen={() => openDrawer(record.id, 'parameter-edit')} />
            )}
        </CustomMorePopover>
    );
};

export const ParameterList = () => {
    const { isLoading, permissions } = usePermissions();
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const filters = useMemo(() => getFilters(isMobile, translate), [isMobile, translate]);

    if (isLoading) {
        return null;
    }
    if (!permissions?.isAdmin && !permissions?.isBok) {
        return null;
    }

    return (
        <CustomList
            resource='parameter'
            title={translate('menu.parameterManagement')}
            filters={filters}
            actions={<ListActions />}
            sort={{ field: 'name', order: 'ASC' }}
            addLastColumnShadow={true}
            empty={false}
        >
            <>
                <ParameterStatisticList />
                <ExpandSignificantParameters />
                <CustomDatagridConfigurable
                    header={<CustomDatagridHeader />}
                    bulkActionButtons={false}
                    expandSingle={false}
                    rowClick='expand'
                    expand={<ParameterDetailsContent />}
                    sx={{
                        '& .RaDatagrid-expandIconCell, & .RaDatagrid-headerCell:first-of-type': {
                            display: 'none',
                        },
                        '& .MuiTableRow-root.MuiTableRow-hover:hover .MuiTableCell-root.column-name': {
                            boxShadow: `-2px 0 0 0 ${Palette.gray.gray300}, 0 -2px 0 0 ${Palette.gray.gray300}, 0 2px 0 0 ${Palette.gray.gray300} !important`,
                        },
                        '& .RaDatagrid-expandedPanel': {
                            backgroundColor: Palette.white,
                            '& .MuiTableCell-root': {
                                '&::before': {
                                    display: 'none',
                                },
                                '&::after': {
                                    display: 'none',
                                },
                            },
                        },
                    }}
                >
                    <TextField source='name' label={translate('resources.parameter.fields.name')} />
                    <TextField source='description' label={translate('resources.parameter.fields.description')} />
                    <TextField source='parameter' sortBy='systemName' label={translate('resources.parameter.fields.parameter')} />
                    <TextField source='value' label={translate('resources.parameter.fields.globalValue')} />
                    <FunctionField
                        source='models'
                        sortBy='models'
                        label={translate('resources.parameter.fields.filters')}
                        render={() => <ParameterModelsField />}
                    />
                    <FunctionField
                        source='overrideCount'
                        sortBy='overrideCount'
                        label={translate('resources.parameter.fields.overwrites')}
                        render={() => <ParameterOverwritesField />}
                    />
                    <CustomBooleanField source='isSignificant' label={translate('resources.parameter.fields.isSignificant')} />
                    <CustomBooleanField source='isActive' label={translate('resources.parameter.fields.isActive')} />
                    <FunctionField
                        label={translate('common.options')}
                        sortable={false}
                        render={() => (
                            <Box onClick={event => event.stopPropagation()}>
                                <MoreActionsPopover />
                            </Box>
                        )}
                    />
                </CustomDatagridConfigurable>
            </>
        </CustomList>
    );
};
